/**
 * Toast Utility (Singleton)
 *
 * A lightweight singleton that delegates to the real ToastContext once
 * initialized. Call `initToast(ctx)` from inside ToastProvider to wire it up.
 *
 * Usage outside React components (e.g. React Query mutation hooks):
 *   import { toast } from '../../utils/toast'
 *   toast.success('Done!')
 */

import { logger } from './logger'

interface ToastFns {
  success(message: string, duration?: number): void
  error(message: string, duration?: number): void
  warning(message: string, duration?: number): void
  info(message: string, durationOrOptions?: number | ToastActionOptions): void
  showToast(type: 'success' | 'error' | 'warning' | 'info', message: string, duration?: number, onClick?: () => void): void
}

export interface ToastActionOptions {
  duration?: number
  onClick?: () => void
}

// Internal delegate — starts as logger fallback until ToastProvider wires real toast
let _toast: ToastFns = {
  success: (msg) => logger.info(`✅ ${msg}`),
  error: (msg) => logger.error(msg),
  warning: (msg) => logger.warn(msg),
  info: (msg) => logger.info(`ℹ️ ${msg}`),
  showToast: (type, msg) => logger.info(`[${type}] ${msg}`),
}

/** Called once by ToastProvider to wire the singleton to the real context. */
export function initToast(fns: ToastFns): void {
  _toast = fns
}

export const toast: ToastFns = {
  success: (...args) => _toast.success(...args),
  error: (...args) => _toast.error(...args),
  warning: (...args) => _toast.warning(...args),
  info: (message, durationOrOptions) => _toast.info(message, durationOrOptions),
  showToast: (...args) => _toast.showToast(...args),
}
